import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useOnlineStatus } from '@/hooks/useOnlineStatus';
import { useConnectionStatus, type ConnectionStatus } from '@/hooks/useConnectionStatus';
import { useOutboxCounts } from '@/features/outbox';
import { Icon } from './Icon';
import './offline-indicator.css';

/**
 * The top-of-app banner: what the connection is doing, and where the user's
 * mail is while it does it.
 *
 * ## The copy, and why it says what it says
 *
 * The owner's request was "put a banner at the top when offline so people know
 * it is stored locally before getting sent when they get back online". So the
 * offline sentence has to carry three facts, not one:
 *
 *  1. **New mail can't be sent right now.** The plain fact, first.
 *  2. **Anything you send is stored on this device.** Where it is. "On this
 *     device" rather than "saved" or "queued", because the thing a user is
 *     actually worried about is whether it exists anywhere at all, and the
 *     honest answer is that it exists *here* and nowhere else yet.
 *  3. **It goes out when you're back online.** What happens next, without a
 *     promise about when.
 *
 * The previous copy — "new messages and sends are paused" — said (1) and left
 * (2) and (3) to be guessed, and "paused" is exactly the word that lets someone
 * assume the server has their message. It was also about to become false.
 *
 * When something is queued the banner says how many, and links to the Outbox,
 * because a count with nowhere to go is a fact the user cannot act on.
 *
 * A message that failed permanently keeps the banner up **even online**: it is
 * the one state where saying nothing would be a silent drop.
 *
 * ## Announced once, not on every render
 *
 * The visible text is `aria-hidden`; a separate visually-hidden live region
 * carries the announcement, and it is rewritten only when the banner's *state
 * key* changes (offline / offline with a count / failures / reconnected).
 * Without that split, every re-render that produced the same sentence would
 * still be a DOM write inside a live region on some engines, and every tick of
 * a changing count would interrupt the screen reader mid-word.
 */

type BannerState = 'offline' | 'failed' | 'reconnected';

export function OfflineIndicator() {
  const online = useOnlineStatus();
  const { waiting, failed } = useOutboxCounts();
  const [showReconnected, setShowReconnected] = useState(false);
  const [wasOffline, setWasOffline] = useState(false);

  useEffect(() => {
    if (!online) {
      setWasOffline(true);
      setShowReconnected(false);
      return;
    }
    if (!wasOffline) return;
    setShowReconnected(true);
    const timer = setTimeout(() => {
      setShowReconnected(false);
      setWasOffline(false);
    }, 3000);
    return () => clearTimeout(timer);
  }, [online, wasOffline]);

  const state: BannerState | undefined = !online
    ? 'offline'
    : failed > 0
      ? 'failed'
      : showReconnected
        ? 'reconnected'
        : undefined;

  // The announcement changes only when the *kind* of thing being said changes,
  // plus the count, which is the part a user needs to hear again when it moves.
  const announceKey = state === undefined ? '' : `${state}:${waiting}:${failed}`;

  if (state === undefined) return null;

  const text =
    state === 'offline'
      ? offlineText(waiting)
      : state === 'failed'
        ? failedText(failed)
        : reconnectedText(waiting);

  return (
    <div className="offline-banner" data-state={state} data-testid="offline-banner">
      <Icon name={state === 'reconnected' ? 'check' : 'offline'} size={16} />
      {/*
        Hidden from assistive tech, because the live region below says the same
        thing: without this the sentence is in the accessibility tree twice.
        The link is NOT inside it -- an `aria-hidden` ancestor would take the
        only action on the banner out of the tree entirely.
      */}
      <span aria-hidden="true">{text}</span>
      {(waiting > 0 || failed > 0) && (
        <Link to="/mail/outbox" className="offline-banner__link">
          Open Outbox
        </Link>
      )}
      <Announcement text={text} announceKey={announceKey} />
    </div>
  );
}

/** Exported so the tests pin the sentences rather than paraphrasing them. */
export function offlineText(waiting: number): string {
  const base =
    "You're offline. New mail can't be sent right now — anything you send is stored on this " +
    "device and goes out when you're back online.";
  if (waiting === 0) return base;
  return waiting === 1
    ? `${base} 1 message is waiting on this device.`
    : `${base} ${waiting} messages are waiting on this device.`;
}

export function reconnectedText(waiting: number): string {
  if (waiting === 0) return 'Back online — syncing your mailbox.';
  return waiting === 1
    ? 'Back online — sending the 1 message waiting on this device.'
    : `Back online — sending the ${waiting} messages waiting on this device.`;
}

export function failedText(failed: number): string {
  return failed === 1
    ? "1 message couldn't be sent. It's still here, in your Outbox."
    : `${failed} messages couldn't be sent. They're still here, in your Outbox.`;
}

/**
 * A live region written to only when `announceKey` changes.
 *
 * `key` on the span, rather than editing it in place: a fresh node is what
 * makes a repeat of the *same* sentence announce again after the state has
 * been away, which is the case that matters when a user goes offline, online
 * and offline again.
 */
function Announcement({ text, announceKey }: { text: string; announceKey: string }) {
  const [announced, setAnnounced] = useState({ key: announceKey, text });
  const lastKey = useRef(announceKey);

  useEffect(() => {
    if (lastKey.current === announceKey) return;
    lastKey.current = announceKey;
    setAnnounced({ key: announceKey, text });
  }, [announceKey, text]);

  return (
    <span className="visually-hidden" role="status" aria-live="polite">
      <span key={announced.key}>{announced.text}</span>
    </span>
  );
}

/**
 * Compact dot + label for the status bar.
 *
 * Three states, the same ones the mail list shows (ADR 0059). It read the
 * two-state hook, so for the several seconds between the interface returning
 * and the backend answering, the status bar still said "Offline" while the
 * list already said "Reconnecting…" -- two parts of one window disagreeing
 * about the same fact.
 */
const DOT_LABEL: Record<ConnectionStatus, string> = {
  online: 'Online',
  offline: 'Offline',
  reconnecting: 'Reconnecting…',
};

export function OfflineStatusDot() {
  const status = useConnectionStatus();
  return (
    <span className="offline-dot" data-online={status === 'online' ? 'true' : 'false'} data-status={status}>
      <span className="offline-dot__mark" aria-hidden="true" />
      {DOT_LABEL[status]}
    </span>
  );
}
